/**
 * 오늘의 돌봄뉴스 카드 — 블로그 체류 훅 H4.
 *
 * 영구지침: docs/develop/frontend/home-page-portal-spec.md §4.3
 *
 * 시각: 최신 1건은 큰 썸네일 + 요약, 나머지 4건은 작은 리스트 (네이버 뉴스 패턴)
 * 데이터: 기존 useBlogPosts 재활용
 */
import { Link } from 'react-router-dom'
import { Newspaper, ChevronRight } from 'lucide-react'
import { useBlogPosts } from '@/features/blog/api'
import { BLOG_CATEGORY_LABELS } from '@/lib/blog/types'
import Skeleton from '@/components/ui/Skeleton'

export default function NewsHeadlineCard() {
  const { data, isLoading } = useBlogPosts({ size: 5 })
  const posts = data?.content ?? []
  const hero = posts[0]
  const list = posts.slice(1, 5)

  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <Newspaper size={16} className="text-cb-primary" />
          오늘의 돌봄뉴스
        </h2>
        <Link
          to="/blog"
          className="flex items-center gap-0.5 text-xs font-medium text-cb-gray-500 hover:text-cb-primary"
        >
          더보기 <ChevronRight size={12} />
        </Link>
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton height="6rem" />
          <Skeleton height="0.875rem" width="80%" />
          <Skeleton height="0.875rem" width="70%" />
        </div>
      ) : posts.length === 0 ? (
        <p className="py-4 text-center text-xs text-cb-gray-400">
          발행된 뉴스가 없어요
        </p>
      ) : (
        <>
          {hero && (
            <Link
              to={`/blog/${hero.slug}`}
              className="group mb-3 flex flex-col gap-2 rounded-xl bg-cb-gray-50 p-3 hover:bg-cb-primary-light/40"
            >
              {hero.thumbnailUrl ? (
                <img
                  src={hero.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="h-32 w-full rounded-lg object-cover"
                />
              ) : (
                <div className="flex h-32 w-full items-center justify-center rounded-lg bg-gradient-to-br from-cb-primary-light to-white">
                  <Newspaper size={28} className="text-cb-primary/40" />
                </div>
              )}
              <div>
                <span className="inline-block rounded bg-cb-primary px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {BLOG_CATEGORY_LABELS[hero.category] ?? hero.category}
                </span>
                <p className="mt-1.5 line-clamp-2 text-sm font-bold leading-snug text-cb-gray-900 group-hover:text-cb-primary">
                  {hero.title}
                </p>
                {hero.summary && (
                  <p className="mt-1 line-clamp-2 text-xs text-cb-gray-500">
                    {hero.summary}
                  </p>
                )}
              </div>
            </Link>
          )}

          <ul className="flex flex-col">
            {list.map((p) => (
              <li key={p.id} className="border-b border-cb-gray-100 last:border-0">
                <Link
                  to={`/blog/${p.slug}`}
                  className="flex items-start gap-2 py-2 hover:bg-cb-gray-50"
                >
                  <span className="mt-1.5 block h-1 w-1 flex-shrink-0 rounded-full bg-cb-gray-300" />
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-cb-gray-700">
                    {p.title}
                  </p>
                  <span className="flex-shrink-0 text-[10px] font-semibold text-cb-gray-400">
                    {BLOG_CATEGORY_LABELS[p.category] ?? p.category}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
